<article class="ts-page">
  <header class="ts-page__head">
    <app-spot kind="sheep" class="ts-page__spot"></app-spot>
    <p class="t-meta ts-page__eyebrow">About</p>
    <h1 class="ts-page__title">Hello, I'm <em>Vincent</em></h1>
    <p class="ts-page__lede">
      I made Talespinner because I think everyone has a story in them,
      and the right tools help more of those stories reach the last page.
    </p>
  </header>

  <section class="ts-about" aria-label="Who makes Talespinner">
    <aside class="ts-card-facts">
      <dl>
        <div>
          <dt class="t-meta">Made in</dt>
          <dd>Arnhem, the Netherlands</dd>
        </div>
        <div>
          <dt class="t-meta">Also writing</dt>
          <dd><a href="https://wingedsheep.com" target="_blank" rel="noopener">wingedsheep.com</a>, about AI, games, music and more</dd>
        </div>
        <div>
          <dt class="t-meta">Away from the keyboard</dt>
          <dd>Hiking and running, best of all in the mountains. Kayaking, bouldering, playing guitar, and playing games (and making a few)</dd>
        </div>
        <div>
          <dt class="t-meta">Say hello</dt>
          <dd><a href="https://discord.gg/mFyF3ePqMF" target="_blank" rel="noopener">On Discord</a> or at <a href="mailto:contact@talespinner.io">contact&#64;talespinner.io</a></dd>
        </div>
      </dl>
    </aside>

    <div class="ts-prose">
      <p class="ts-about__opening">
        My fascination with machine learning started when I programmed a computer to play Othello.
        Andrew Ng's course on Coursera showed me how much further it could go, and what stayed with me
        most was the idea of using it to make new things.
      </p>
      <p>
        Talespinner is where that led. It's a writing studio where your ideas meet an AI's patience:
        a place for writers, seasoned or just starting, to tinker with a story as much as they like,
        try things, throw them away, and keep the parts that sound like them.
      </p>
      <blockquote class="ts-about__quote">
        You hold the pen. Talespinner keeps the world, the characters and the plot in its head, so you can keep writing.
      </blockquote>
      <p>
        Your stories and your feedback are what move it forward. If something is missing, or could
        be better, I'd love to hear about it.
      </p>
    </div>
  </section>

  <section class="ts-block" id="technology" aria-labelledby="ts-tech-title" tsReveal>
    <div class="ts-about__split">
      <div class="ts-block__head">
        <p class="t-meta ts-block__eyebrow">§ Under the hood</p>
        <h2 class="ts-block__title" id="ts-tech-title">The models you can write with</h2>
        <p class="ts-about__lede">
          Talespinner gives you several frontier language models, each with its own strengths.
          Pick one per project, or switch for a single scene.
        </p>
        @if (smallestContext; as context) {
          <p class="ts-about__context">
            Every one of them can keep at least {{ context.tokens | number }} tokens in view: about
            {{ context.words | number }} words, or {{ context.pages | number }} pages of a novel.
          </p>
        }
      </div>

      <ul class="ts-models">
        @for (model of models; track model.value) {
          <li class="ts-models__item">
            <p class="ts-models__name">{{ model.name }} <span class="t-caption">{{ model.provider }}</span></p>
            <p class="ts-models__description">{{ model.description }}</p>
          </li>
        }
      </ul>
    </div>
  </section>

  <section class="ts-cta" aria-labelledby="ts-about-cta" tsReveal>
    <h2 class="ts-cta__title" id="ts-about-cta">Ideas, bugs, feature requests: I read all of it.</h2>
    <div class="ts-cta__actions">
      <a class="ts-btn ts-btn--secondary ts-btn--lg" href="https://discord.gg/mFyF3ePqMF" target="_blank" rel="noopener">
        <app-discord-icon></app-discord-icon> Join the Discord
      </a>
      <a class="ts-btn ts-btn--ghost ts-btn--lg" href="mailto:contact@talespinner.io">Send an email</a>
    </div>
  </section>
</article>
